'use client';

import { useEffect, useRef, useState } from 'react';
import { WIDGET_REGISTRY } from '../widgets/registry';
import { WidgetCard } from './widget-card';
import { useDashboardLayout } from '../hooks/use-dashboard-layout';
import type { DashboardLayout } from '../widgets/types';

declare global {
  interface Window {
    Sortable?: any;
  }
}

const SORTABLE_CDN = 'https://cdn.jsdelivr.net/npm/sortablejs@1.15.0/Sortable.min.js';

const DEFAULT_LAYOUT: DashboardLayout = {
  visible: [
    'revenue-month',
    'invoiced-today',
    'collected-today',
    'collected-month',
    'outstanding',
    'dso',
    'cash-position',
    'aging',
    'anaf-status',
    'vat-donut',
    'declaratii',
    'spv-status',
    'collection-rate',
    'yoy-comparison',
    'overdue-trend',
    'alerts',
    'upcoming-recurring',
    'detected-recurring',
    'activity-feed',
    'cashflow-forecast',
    'whatif',
    'revenue-chart',
    'top-clients',
    'top-debitors',
    'recent-invoices',
    'quick-actions',
  ],
  hidden: [],
};

function loadSortable(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('no window'));
    if (window.Sortable) return resolve();
    const existing = document.querySelector<HTMLScriptElement>('script[data-sortable]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = SORTABLE_CDN;
    s.async = true;
    s.setAttribute('data-sortable', '1');
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('failed to load sortable'));
    document.head.appendChild(s);
  });
}

interface Props {
  onAddClick?: () => void;
}

export function WidgetGrid({ onAddClick }: Props) {
  const { layout, hydrated, setOrder, hideWidget } = useDashboardLayout(DEFAULT_LAYOUT);
  const gridRef = useRef<HTMLDivElement>(null);
  const sortableRef = useRef<any>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadSortable()
      .then(() => setReady(true))
      .catch(() => setReady(true)); // render without drag if CDN fails
  }, []);

  useEffect(() => {
    if (!ready || !hydrated || !gridRef.current || !window.Sortable) return;
    if (sortableRef.current) return;
    sortableRef.current = window.Sortable.create(gridRef.current, {
      animation: 200,
      easing: 'cubic-bezier(0.2, 0.9, 0.3, 1.2)',
      ghostClass: 'dash-sortable-ghost',
      chosenClass: 'dash-sortable-chosen',
      dragClass: 'dash-sortable-drag',
      handle: '[data-drag-grip]', // drag DOAR de pe grip-ul din antet → restul cardului scrollabil pe touch (fix mobil)
      onEnd: () => {
        if (!gridRef.current) return;
        const children = Array.from(gridRef.current.children) as HTMLElement[];
        const newOrder = children
          .map((el) => el.getAttribute('data-widget-id'))
          .filter((v): v is string => Boolean(v));
        setOrder(newOrder);
      },
    });
    return () => {
      sortableRef.current?.destroy();
      sortableRef.current = null;
    };
  }, [ready, hydrated, setOrder]);

  if (!hydrated) {
    return (
      <div className="grid grid-cols-12 gap-4 min-h-[400px]">
        <div className="col-span-12 h-40 bg-neutral-100 dark:bg-neutral-800 animate-pulse rounded-2xl" />
      </div>
    );
  }

  return (
    <>
      <div ref={gridRef} className="grid grid-cols-12 gap-4 grid-flow-dense dash-uniform-rows">
        {layout.visible.map((id) => {
          const def = WIDGET_REGISTRY[id];
          if (!def) return null;
          const SIZE_TO_SPAN: Record<string, number> = { S: 3, M: 4, L: 6, XL: 8, Full: 12 };
          const SIZE_TO_ROW: Record<string, number> = { S: 1, M: 1, L: 2, XL: 2, Full: 2 };
          const span = def.defaultSpan ?? SIZE_TO_SPAN[(def as any).size] ?? 4;
          const rowSpan = def.defaultRow ?? SIZE_TO_ROW[(def as any).size] ?? 1;
          const Component = def.Component;
          const colClass =
            span === 2 ? 'col-span-12 sm:col-span-6 lg:col-span-2'
              : span === 3 ? 'col-span-12 sm:col-span-6 lg:col-span-3'
              : span === 4 ? 'col-span-12 sm:col-span-6 lg:col-span-4'
              : span === 5 ? 'col-span-12 lg:col-span-5'
              : span === 6 ? 'col-span-12 lg:col-span-6'
              : span === 7 ? 'col-span-12 lg:col-span-7'
              : span === 8 ? 'col-span-12 lg:col-span-8'
              : span === 9 ? 'col-span-12 lg:col-span-9'
              : 'col-span-12';
          const rowClass = rowSpan === 2 ? 'row-span-2' : '';
          return (
            <div key={id} data-widget-id={id} className={colClass + ' ' + rowClass}>
              <WidgetCard
                id={id}
                title={def.title}
                icon={def.icon}
                isNew={def.isNew}
                onHide={hideWidget}
              >
                <Component />
              </WidgetCard>
            </div>
          );
        })}
      </div>

      <style jsx global>{`
        /* Row-height uniform — carduri același înălțime pe același rând */
        .dash-uniform-rows { grid-auto-rows: minmax(200px, auto); }
        .dash-uniform-rows > div { min-height: 200px; }
        .dash-uniform-rows > div.row-span-2 { min-height: 416px; }
        .dash-uniform-rows > div > div { height: 100%; }
      `}</style>
      <style jsx global>{`
        .dash-sortable-ghost {
          opacity: 0.25;
        }
        .dash-sortable-chosen {
          cursor: grabbing;
        }
        .dash-sortable-drag {
          cursor: grabbing;
          box-shadow: 0 20px 40px rgba(241, 90, 36, 0.25) !important;
          transform: rotate(-0.5deg);
        }
      `}</style>
    </>
  );
}
